/**
 * Admin Transcriptions - Browse all + Search PTT transcriptions
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Search, Mic, Hash, Clock, Globe, RefreshCw, List } from 'lucide-react';
import axios from 'axios';

function formatTime(ts) {
  return new Date(ts).toLocaleString('es-MX', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatDuration(secs) {
  if (!secs) return '—';
  return secs >= 60
    ? `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`
    : `${Math.round(secs)}s`;
}

const LANG_FLAGS = { es: '🇲🇽', en: '🇺🇸', fr: '🇫🇷', de: '🇩🇪', pt: '🇧🇷', auto: '🌐' };

function TranscriptionCard({ tx }) {
  const flag = LANG_FLAGS[tx.language] || '🌐';
  return (
    <div className="rounded-xl p-4 transition-colors hover:border-brand-500/30"
      style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,0,0,0.05)' }}>
      <div className="flex items-start gap-3">
        {/* Avatar dot */}
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold"
          style={{ background: tx.User?.avatarColor || '#007AFF', color: '#000' }}>
          {tx.User?.username?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-n-800">{tx.User?.username || '—'}</span>
            <span className="text-xs text-n-400">en</span>
            <span className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(0,0,0,0.06)', color: '#007AFF' }}>
              #{tx.Channel?.name || '—'}
            </span>
            <span className="text-xs text-n-400">{formatDuration(tx.duration)}</span>
            {tx.language && (
              <span className="text-xs">{flag} {tx.language}</span>
            )}
            <span className="ml-auto text-xs text-n-400">{formatTime(tx.startedAt)}</span>
          </div>
          {/* Transcription text */}
          <p className="text-sm text-n-800 italic leading-relaxed">
            "{tx.transcription}"
          </p>
        </div>
      </div>
    </div>
  );
}

function RecentTab() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [channelFilter, setChannelFilter] = useState('');

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 30 };
      if (channelFilter) params.channelId = channelFilter;
      const res = await axios.get('/api/admin/transcriptions/recent', { params });
      setData(res.data.data);
      setTotal(res.data.total);
      setPage(res.data.page);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [channelFilter]);

  useEffect(() => { load(1); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-n-500">{total} transcripciones en total</span>
        <button onClick={() => load(page)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors"
          style={{ background: 'rgba(0,0,0,0.05)', color: '#007AFF', border: '1px solid rgba(0,122,255,0.20)' }}>
          <RefreshCw size={12} /> Actualizar
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-n-400">Cargando...</div>
      ) : data.length === 0 ? (
        <div className="text-center py-12">
          <Mic size={32} className="mx-auto mb-3 text-n-300" />
          <p className="text-n-500">No hay transcripciones aún</p>
          <p className="text-xs text-n-300 mt-1">Whisper procesa los mensajes PTT automáticamente</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {data.map(tx => <TranscriptionCard key={tx.id} tx={tx} />)}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button disabled={page <= 1} onClick={() => load(page - 1)}
            className="px-3 py-1.5 rounded-lg text-xs disabled:opacity-40 transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}>
            ← Anterior
          </button>
          <span className="text-xs text-n-500">{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => load(page + 1)}
            className="px-3 py-1.5 rounded-lg text-xs disabled:opacity-40 transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}>
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
}

function SearchTab() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const search = useCallback(async (p = 1) => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const params = { q: query.trim(), page: p, limit: 30 };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await axios.get('/api/admin/transcriptions/search', { params });
      setResults(res.data.data);
      setTotal(res.data.total);
      setPage(res.data.page);
      setTotalPages(res.data.totalPages);
      setSearched(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [query, dateFrom, dateTo]);

  return (
    <div>
      {/* Search controls */}
      <div className="rounded-xl p-4 mb-5"
        style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,0,0,0.06)' }}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-n-500" />
            <input
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-n-800 text-sm placeholder-n-400 focus:outline-none"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
              placeholder='Buscar: "emergencia", "falla", nombre...'
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && search(1)}
            />
          </div>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-2 rounded-xl text-sm text-n-600 focus:outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }} />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="px-3 py-2 rounded-xl text-sm text-n-600 focus:outline-none"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }} />
          <button onClick={() => search(1)} disabled={!query.trim() || loading}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-40"
            style={{ background: '#007AFF', color: '#000' }}>
            {loading ? '...' : 'Buscar'}
          </button>
        </div>
      </div>

      {!searched ? (
        <div className="text-center py-12">
          <Search size={32} className="mx-auto mb-3 text-n-300" />
          <p className="text-n-500">Escribe algo para buscar en el historial</p>
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-12 text-n-500">Sin resultados para "{query}"</div>
      ) : (
        <>
          <p className="text-xs text-n-400 mb-3">{total} resultado{total !== 1 ? 's' : ''}</p>
          <div className="flex flex-col gap-3">
            {results.map(tx => <TranscriptionCard key={tx.id} tx={tx} />)}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button disabled={page <= 1} onClick={() => search(page - 1)}
                className="px-3 py-1.5 rounded-lg text-xs disabled:opacity-40"
                style={{ background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}>← Anterior</button>
              <span className="text-xs text-n-500">{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => search(page + 1)}
                className="px-3 py-1.5 rounded-lg text-xs disabled:opacity-40"
                style={{ background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}>Siguiente →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Transcriptions() {
  const [tab, setTab] = useState('recent');

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-n-800">Transcripciones</h1>
        <p className="text-sm text-n-500 mt-0.5">Historial completo de mensajes PTT transcritos por Whisper</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl w-fit"
        style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {[
          { key: 'recent', label: 'Recientes', icon: <List size={14} /> },
          { key: 'search', label: 'Buscar', icon: <Search size={14} /> },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={tab === t.key
              ? { background: '#007AFF', color: '#000' }
              : { color: '#64748b' }}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {tab === 'recent' ? <RecentTab /> : <SearchTab />}
    </div>
  );
}
